import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import { DashboardContext } from "../contexts/DashboardContext"; // adjust path if needed
import { useAuth } from "../contexts/AuthContext"; // adjust path if needed
import {
  format,
  startOfDay,
  subDays,
  isSameDay,
  differenceInCalendarDays,
} from "date-fns";

// -------------------------
// Tasks Analysis Page
// Includes 10 sections described earlier (all included)
// This file assumes you already provide tasks via DashboardContext (tasks: []).
// If your app uses a different provider, change the imports accordingly.
// -------------------------

export default function TasksAnalysis() {
  const { tasks = [], percentages = [] } = useContext(DashboardContext) || {};
  const { userId } = useAuth() || {};

  // Normalize tasks: ensure fields exist and convert Firestore timestamps
  const normTasks = useMemo(() => {
    const toDate = (v) => {
      if (!v) return null;
      if (typeof v.toDate === "function") return v.toDate();
      if (v instanceof Date) return v;
      return new Date(v);
    };

    return (tasks || []).map((t) => ({
      id: t.id,
      text: t.text || "(no title)",
      clientId: t.clientId || null,
      category: t.category || t.clientId ? "Client" : "General",
      recurring: !!t.recurring,
      days: t.days || [],
      date: toDate(t.date) || null,
      createdAt: toDate(t.createdAt) || null,
      completed: !!t.completed,
      completedDates: t.completedDates || {},
      pushedDates: t.pushedDates || {},
      estimatedMinutes: t.estimatedMinutes || null,
      loggedMinutes: t.loggedMinutes || null,
      difficulty: t.difficulty || "medium", // expected: easy | medium | hard
    }));
  }, [tasks]);

  // ---------- 1. Overall Productivity Score ----------
  // Simple heuristic: completion ratio over last 7 days + streak influence
  const last7Days = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) =>
      startOfDay(subDays(new Date(), 6 - i))
    );
  }, []);

  const { productivityScore, completionPercent } = useMemo(() => {
    // completion across last 7 days (use percentages if provided)
    const recentPercentages =
      percentages?.slice(-7).map((p) => p.percentage) || [];
    const avgPct = recentPercentages.length
      ? Math.round(
          recentPercentages.reduce((a, b) => a + b, 0) /
            recentPercentages.length
        )
      : null;

    // fallback: compute from tasks (today and recent)
    const computeFromTasks = () => {
      const todayKey = format(startOfDay(new Date()), "yyyy-MM-dd");
      const todays = normTasks.filter((t) => {
        // a very simple inclusion test: recurring with empty days or today's date
        if (t.recurring) return true;
        if (t.date) return isSameDay(t.date, new Date());
        if (t.createdAt) return isSameDay(t.createdAt, new Date());
        return false;
      });
      const completed = todays.filter(
        (t) => t.completed || t.completedDates?.[todayKey]
      ).length;
      const total = todays.length || 1;
      return Math.round((completed / total) * 100);
    };

    const fallback = computeFromTasks();
    const cp = avgPct ?? fallback;

    // Streak bonus (small): compute current streak of days with >=1 completed
    let streak = 0;
    for (let i = 0; i < 30; i++) {
      const day = startOfDay(subDays(new Date(), i));
      const dayKey = format(day, "yyyy-MM-dd");
      const hasCompleted = normTasks.some((t) =>
        t.recurring ? t.completedDates?.[dayKey] : t.completed
      );
      if (hasCompleted) streak++;
      else break;
    }

    // Score is completionPercent with small bonus for streak (max +10)
    const score = Math.min(
      100,
      (cp || 0) + Math.min(10, Math.floor(streak / 2))
    );

    return { productivityScore: score, completionPercent: cp };
  }, [normTasks, percentages]);

  // ---------- 2. Task Completion Rate breakdown ----------
  const completionBreakdown = useMemo(() => {
    const totals = { completed: 0, inprogress: 0, notStarted: 0, overdue: 0 };
    const today = startOfDay(new Date());
    const todayKey = format(today, "yyyy-MM-dd");

    normTasks.forEach((t) => {
      const completedToday = t.recurring
        ? !!t.completedDates?.[todayKey]
        : !!t.completed;
      const hasDate = !!t.date;

      if (completedToday) totals.completed++;
      else if (hasDate && t.date < today) totals.overdue++;
      else if (hasDate) totals.inprogress++;
      else totals.notStarted++;
    });

    const total = Object.values(totals).reduce((a, b) => a + b, 0) || 1;
    const perc = Object.fromEntries(
      Object.entries(totals).map(([k, v]) => [k, Math.round((v / total) * 100)])
    );
    return { totals, perc };
  }, [normTasks]);

  // ---------- 3. Time Spent vs Estimated ----------
  const timeStats = useMemo(() => {
    let estimated = 0;
    let logged = 0;
    let countWithEstimate = 0;
    normTasks.forEach((t) => {
      if (t.estimatedMinutes) {
        estimated += t.estimatedMinutes;
        countWithEstimate++;
      }
      if (t.loggedMinutes) logged += t.loggedMinutes;
    });
    const avgDeviation = countWithEstimate
      ? Math.round(((logged - estimated) / (estimated || 1)) * 100)
      : null;
    return { estimated, logged, avgDeviation };
  }, [normTasks]);

  // ---------- 4. Category / Project Breakdown ----------
  const categoryBreakdown = useMemo(() => {
    const map = {};
    normTasks.forEach((t) => {
      const cat = t.category || "General";
      map[cat] = (map[cat] || 0) + 1;
    });
    return map;
  }, [normTasks]);

  // ---------- 5. Weekly / Monthly Activity Timeline ----------
  const weeklyActivity = useMemo(() => {
    // tasks completed per last 7 days
    return last7Days.map((day) => {
      const key = format(day, "yyyy-MM-dd");
      const completedCount = normTasks.filter((t) =>
        t.recurring
          ? !!t.completedDates?.[key]
          : t.completed &&
            (t.date ? isSameDay(t.date, day) : isSameDay(t.createdAt, day))
      ).length;
      return { day: format(day, "EEE"), count: completedCount };
    });
  }, [normTasks, last7Days]);

  // ---------- 6. Difficulty breakdown ----------
  const difficultyCounts = useMemo(() => {
    const d = { easy: 0, medium: 0, hard: 0 };
    normTasks.forEach((t) => {
      const k = (t.difficulty || "medium").toLowerCase();
      d[k] = (d[k] || 0) + 1;
    });
    return d;
  }, [normTasks]);

  // ---------- 7. Streak Counter ----------
  const streak = useMemo(() => {
    let s = 0;
    for (let i = 0; i < 365; i++) {
      const day = startOfDay(subDays(new Date(), i));
      const key = format(day, "yyyy-MM-dd");
      const completed = normTasks.some((t) =>
        t.recurring
          ? !!t.completedDates?.[key]
          : t.completed &&
            (t.date ? isSameDay(t.date, day) : isSameDay(t.createdAt, day))
      );
      if (completed) s++;
      else break;
    }
    return s;
  }, [normTasks]);

  // ---------- 8. Overdue Tasks Insights ----------
  const overdueInsights = useMemo(() => {
    const today = startOfDay(new Date());
    const overdue = normTasks.filter(
      (t) => t.date && t.date < today && !t.completed
    );
    const avgDaysLate = overdue.length
      ? Math.round(
          overdue.reduce(
            (acc, t) => acc + differenceInCalendarDays(today, t.date),
            0
          ) / overdue.length
        )
      : 0;

    // category with most overdue
    const map = {};
    overdue.forEach((t) => {
      const c = t.category || "General";
      map[c] = (map[c] || 0) + 1;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return { count: overdue.length, avgDaysLate, byCategory: sorted };
  }, [normTasks]);

  // ---------- 9. Task Efficiency Feedback (simple rules) ----------
  const feedbackText = useMemo(() => {
    const comments = [];
    if (completionPercent !== null) {
      if (completionPercent < 30)
        comments.push(
          "Your completion rate is low — try breaking tasks into smaller steps."
        );
      else if (completionPercent < 70)
        comments.push(
          "Good consistency — aim for small wins earlier in the day."
        );
      else comments.push("Great job — keep the momentum!");
    }

    if (
      difficultyCounts.hard < difficultyCounts.medium &&
      difficultyCounts.easy > difficultyCounts.medium
    ) {
      comments.push(
        "Most of your tasks are easy. Consider scheduling 1–2 medium tasks each day to grow progress."
      );
    }

    if (overdueInsights.count > 0) {
      comments.push(
        `You have ${overdueInsights.count} overdue tasks (avg ${overdueInsights.avgDaysLate} days late). Prioritize them this week.`
      );
    }

    // best day detection (which weekday has most completions)
    const byWeekday = {};
    normTasks.forEach((t) => {
      const created = t.createdAt || t.date;
      if (!created) return;
      const dow = format(created, "EEEE");
      byWeekday[dow] = (byWeekday[dow] || 0) + 1;
    });
    const bestDay = Object.entries(byWeekday).sort((a, b) => b[1] - a[1])[0];
    if (bestDay)
      comments.push(
        `You tend to add/complete most tasks on ${bestDay[0]}. Consider scheduling focus time then.`
      );

    return comments;
  }, [completionPercent, difficultyCounts, overdueInsights, normTasks]);

  // ---------- 10. Upcoming Priorities (Top 3) ----------
  const upcoming = useMemo(() => {
    const today = startOfDay(new Date());
    const candidates = normTasks
      .filter((t) => !t.completed)
      .map((t) => ({
        ...t,
        priorityScore:
          (t.difficulty === "hard" ? 3 : t.difficulty === "medium" ? 2 : 1) -
          (t.date ? differenceInCalendarDays(t.date || today, today) * 0.1 : 0),
      }))
      .sort((a, b) => b.priorityScore - a.priorityScore);
    return candidates.slice(0, 3);
  }, [normTasks]);

  // ---------- Small helpers for visual components ----------
  const Donut = ({ segments = [], size = 120 }) => {
    const stroke = 14;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    let offset = 0;
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <filter id="s" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.08" />
          </filter>
        </defs>
        <g transform={`rotate(-90 ${center} ${center})`}>
          {segments.map((seg, i) => {
            const length = (seg.value / 100) * circumference;
            const dashArray = `${length} ${circumference - length}`;
            const path = (
              <circle
                key={i}
                r={radius}
                cx={center}
                cy={center}
                fill="transparent"
                stroke={seg.color}
                strokeWidth={stroke}
                strokeDasharray={dashArray}
                strokeDashoffset={-offset}
                style={{ transition: "stroke-dashoffset 0.5s, stroke 0.3s" }}
              />
            );
            offset += length;
            return path;
          })}
        </g>
      </svg>
    );
  };

  const BarSimple = ({ data = [], height = 80 }) => {
    const max = Math.max(...data.map((d) => d.count), 1);
    return (
      <Bars>
        {data.map((d) => (
          <Bar
            key={d.day}
            style={{ height: `${(d.count / max) * height}px` }}
            title={`${d.day}: ${d.count}`}
          >
            <span>{d.count}</span>
          </Bar>
        ))}
      </Bars>
    );
  };

  return (
    <Page>
      <TopRow>
        <Card large>
          <H2>Overall Productivity</H2>
          <FlexRow>
            <Donut
              size={140}
              segments={[
                { value: productivityScore, color: "#22c55e" },
                { value: 100 - productivityScore, color: "#e5e7eb" },
              ]}
            />
            <div style={{ marginLeft: 16 }}>
              <BigNumber>{productivityScore}</BigNumber>
              <Muted>Productivity score (0 - 100)</Muted>
              <TinyStat>
                {completionPercent ?? "—"}% average completion (last 7d)
              </TinyStat>
              <TinyStat>
                Current streak: <strong>{streak} day(s)</strong>
              </TinyStat>
            </div>
          </FlexRow>
        </Card>

        <Card>
          <H3>Completion Breakdown</H3>
          <MiniRow>
            <Donut
              size={100}
              segments={[
                {
                  value: completionBreakdown.perc.completed || 0,
                  color: "#10b981",
                },
                {
                  value: completionBreakdown.perc.inprogress || 0,
                  color: "#facc15",
                },
                {
                  value: completionBreakdown.perc.notStarted || 0,
                  color: "#60a5fa",
                },
                {
                  value: completionBreakdown.perc.overdue || 0,
                  color: "#f87171",
                },
              ]}
            />
            <Legend>
              <LegendRow>
                <LegendDot style={{ background: "#10b981" }} /> Completed{" "}
                {completionBreakdown.totals.completed}
              </LegendRow>
              <LegendRow>
                <LegendDot style={{ background: "#facc15" }} /> In progress{" "}
                {completionBreakdown.totals.inprogress}
              </LegendRow>
              <LegendRow>
                <LegendDot style={{ background: "#60a5fa" }} /> Not started{" "}
                {completionBreakdown.totals.notStarted}
              </LegendRow>
              <LegendRow>
                <LegendDot style={{ background: "#f87171" }} /> Overdue{" "}
                {completionBreakdown.totals.overdue}
              </LegendRow>
            </Legend>
          </MiniRow>
        </Card>
      </TopRow>

      <Grid>
        <Card>
          <H3>Time: Estimated vs Logged</H3>
          <p style={{ marginBottom: 8 }}>
            Estimated: <strong>{timeStats.estimated} min</strong> — Logged:{" "}
            <strong>{timeStats.logged} min</strong>
            {timeStats.avgDeviation !== null && (
              <span>
                {" "}
                — Avg deviation: <strong>{timeStats.avgDeviation}%</strong>
              </span>
            )}
          </p>
          <ProgressRow>
            <SmallBar
              style={{
                width: `${Math.min(
                  100,
                  (timeStats.logged / (timeStats.estimated || 1)) * 100
                )}%`,
              }}
            />
          </ProgressRow>
        </Card>

        <Card>
          <H3>Category Breakdown</H3>
          <CategoryList>
            {Object.entries(categoryBreakdown).map(([k, v]) => (
              <CategoryRow key={k}>
                <strong>{k}</strong>
                <span>{v}</span>
              </CategoryRow>
            ))}
          </CategoryList>
        </Card>

        <Card>
          <H3>Weekly Activity</H3>
          <BarSimple data={weeklyActivity} height={60} />
        </Card>

        <Card>
          <H3>Difficulty Distribution</H3>
          <DifficultyRow>
            <DifficultyBlock>
              <strong>Easy</strong>
              <span>{difficultyCounts.easy}</span>
            </DifficultyBlock>
            <DifficultyBlock>
              <strong>Medium</strong>
              <span>{difficultyCounts.medium}</span>
            </DifficultyBlock>
            <DifficultyBlock>
              <strong>Hard</strong>
              <span>{difficultyCounts.hard}</span>
            </DifficultyBlock>
          </DifficultyRow>
        </Card>

        <Card>
          <H3>Overdue Insights</H3>
          <p>
            {overdueInsights.count} overdue tasks — avg{" "}
            {overdueInsights.avgDaysLate} days late
          </p>
          {overdueInsights.byCategory.length > 0 && (
            <div>
              <small>Top overdue categories:</small>
              <ul>
                {overdueInsights.byCategory.slice(0, 3).map(([cat, n]) => (
                  <li key={cat}>
                    {cat} — {n}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <Card>
          <H3>Streak</H3>
          <LargeStat>{streak}</LargeStat>
          <small>Days in a row with at least one completed task</small>
        </Card>

        <Card>
          <H3>AI-like Feedback</H3>
          <ul>
            {feedbackText.length ? (
              feedbackText.map((c, i) => <li key={i}>{c}</li>)
            ) : (
              <li>No insights yet — complete a few tasks and revisit.</li>
            )}
          </ul>
        </Card>

        <Card>
          <H3>Upcoming Priorities</H3>
          {upcoming.length ? (
            <ul>
              {upcoming.map((t) => (
                <li key={t.id}>
                  <strong>{t.text}</strong> — {t.difficulty}{" "}
                  {t.date ? `• due ${format(t.date, "MMM d")}` : ""}
                </li>
              ))}
            </ul>
          ) : (
            <p>No upcoming priorities</p>
          )}
        </Card>
      </Grid>
    </Page>
  );
}

// ---------------- Styles ----------------
const Page = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: var(--fontPrimaryColor, #0f172a);
`;
const TopRow = styled.div`
  display: flex;
  gap: 1rem;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;
const Card = styled.div`
  background: var(--secondaryBackgroundColor, #fff);
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(2, 6, 23, 0.06);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  ${(p) => p.large && `flex:1 1 60%;`}
`;
const H2 = styled.h2`
  font-size: 1.1rem;
  margin: 0;
`;
const H3 = styled.h3`
  font-size: 0.95rem;
  margin: 0;
`;
const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;
const BigNumber = styled.div`
  font-size: 2.6rem;
  font-weight: 700;
`;
const TinyStat = styled.div`
  font-size: 0.8rem;
  color: var(--secondaryFontColor, #6b7280);
`;
const Muted = styled.div`
  color: var(--secondaryFontColor, #6b7280);
  font-size: 0.9rem;
`;
const MiniRow = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;
`;
const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
`;
const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
`;
const ProgressRow = styled.div`
  height: 10px;
  background: #f3f4f6;
  border-radius: 999px;
  overflow: hidden;
`;
const SmallBar = styled.div`
  height: 100%;
  background: #60a5fa;
  border-radius: 999px;
  transition: width 0.4s;
`;
const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const CategoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px dashed #eee;
`;
const Bars = styled.div`
  display: flex;
  align-items: end;
  gap: 8px;
  height: 80px;
  padding: 8px 0;
`;
const Bar = styled.div`
  flex: 1;
  background: #e6eefc;
  border-radius: 6px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 4px 2px;
  color: #0f172a;
  font-weight: 600;
`;
const DifficultyRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const DifficultyBlock = styled.div`
  flex: 1;
  background: #f8fafc;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const LargeStat = styled.div`
  font-size: 2rem;
  font-weight: 700;
`;

// export nothing else
